import { folderApi } from './api.service';

// Define the shape of a project as returned by the folder endpoints
export interface Project {
  testProjectID: number;
  name: string;
  remark?: string | null;
  lastModifiedTime?: string | null;
  channelType?: string | null;
}

// The raw folder object as returned by /folders/{parentId}/children
export interface FolderApi {
  folderId: number;
  name: string;
  parentId: number | null;
  projects: Project[];
}

// A Folder with nested subfolders
export interface Folder extends FolderApi {
  folders: Folder[];
}

// Fetch direct children (folders + projects) for a given parent folder
export async function fetchFolderChildren(parentId: number): Promise<FolderApi[]> {
  try {
    const response = await folderApi.get<FolderApi[]>(`/folders/${parentId}/children`);
    // Normalize response: if API wraps result in `.data`, unwrap it
    const payload = (response.data as any);
    let items: any;
    if (Array.isArray(payload)) {
      items = payload;
    } else if (payload && Array.isArray(payload.data)) {
      items = payload.data;
    } else {
      items = [];
    }
    return items as FolderApi[];
  } catch (error) {
    console.error(`Failed to fetch children for folder ${parentId}`, error);
    return [];
  }
}

export async function renameFolderApi(folderId: number, name: string): Promise<void> {
  try {
    await folderApi.put(`/folders/${folderId}/rename?name=${encodeURIComponent(name)}`);
  } catch (error) {
    console.error(`Failed to rename folder ${folderId}`, error);
    throw error;
  }
}

// Define the body structure for creating a folder
interface CreateFolderPayload {
  name: string;
  parentId: number | null;
  projects: []; // Always empty for new folders
}

// Create a new folder
export async function createFolderApi(name: string, parentId: number | null): Promise<FolderApi> {
  const payload: CreateFolderPayload = {
    name,
    parentId,
    projects: [],
  };
  try {
    // Assuming the API returns the newly created folder object
    const response = await folderApi.post<FolderApi>('/folders', payload);
    // Normalize potential API response wrapping
    const responseData = (response.data as any);
    if (responseData && typeof responseData === 'object' && 'folderId' in responseData) {
        return responseData as FolderApi;
    }
    // Handle cases where the data might be nested, e.g., in response.data.data
    if (responseData && responseData.data && typeof responseData.data === 'object' && 'folderId' in responseData.data) {
        return responseData.data as FolderApi;
    }
    // If the response is unexpected, throw an error
    console.error('Unexpected response structure from createFolderApi:', response.data);
    throw new Error('Failed to create folder: Unexpected API response');

  } catch (error) {
    console.error(`Failed to create folder with parent ${parentId}`, error);
    throw error; // Re-throw to be handled by the caller
  }
}

// Delete a folder
export async function deleteFolderApi(folderId: number): Promise<void> {
  try {
    // Assuming the API returns 200/204 on success, and throws error otherwise
    await folderApi.delete(`/folders/${folderId}`);
  } catch (error) {
    console.error(`Failed to delete folder ${folderId}`, error);
    // We re-throw the error so the calling component can inspect it
    // (e.g., check error.response.status for 400)
    throw error;
  }
}

// Add moveProjectApi: move a project from one folder to another
export async function moveProjectApi(
  projectId: number,
  srcFolderId: number,
  destFolderId: number
): Promise<void> {
  try {
    await folderApi.post(
      `/folders/moveProject?projectId=${projectId}&srcFolderId=${srcFolderId}&destFolderId=${destFolderId}`
    );
  } catch (error) {
    console.error(
      `Failed to move project ${projectId} from folder ${srcFolderId} to folder ${destFolderId}`,
      error
    );
    throw error;
  }
}

// Copy a project into a destination folder
export async function copyProjectApi(
  projectId: number,
  destFolderId: number
): Promise<void> {
  try {
    await folderApi.post(
      `/folders/copyProject?projectId=${projectId}&folderId=${destFolderId}`
    );
  } catch (error) {
    console.error(
      `Failed to copy project ${projectId} to folder ${destFolderId}`,
      error
    );
    throw error;
  }
} 